// Geteilter Cache für Betman-Stories über ALLE BetmanStoryWidget-Instanzen hinweg (z.B. eine auf
// der Startseite, eine auf der Tipp-Seite) - der Story-Endpunkt soll pro identischer Anfrage nur
// einmal aufgerufen werden, nicht einmal pro Widget-Instanz. Gleiches Muster wie der
// geteilterCache in useSpielplanData.js.
//
// ZWEI Zugänge:
// - useBetmanStories(props): reaktiv ans Widget gebunden, fürs Laden der Story-REIHE (fixer
//   Zeitraum aus den Props)
// - ladeStoriesFuerZeitraum(...): einmaliger Abruf für BELIEBIGEN Zeitraum, fürs interaktive
//   Filtern in der Vollbild-Übersicht (Nutzer wählt selbst "letzte Woche"/"letzter Monat"/etc.)
import { ref } from 'vue'
import { vollstaendigeBildUrl } from '../lib/story.js'

const geteilterCache = new Map() // cacheKey -> { stories, loading, error, ladeversprechen }

function ermittleCacheKey({ apiBase, von, bis, bildBasisUrl }) {
  return JSON.stringify({
    apiBase,
    von: von || null,
    bis: bis || null,
    bildBasisUrl: bildBasisUrl || null
  })
}

function heutePlusTage(tage) {
  const d = new Date()
  d.setDate(d.getDate() + tage)
  return d.toISOString().slice(0, 10) // 'YYYY-MM-DD'
}

function ermittleOderErstelleEintrag(cacheKey) {
  let eintrag = geteilterCache.get(cacheKey)
  if (!eintrag) {
    eintrag = {
      stories: ref([]),
      loading: ref(true),
      error: ref(null),
      ladeversprechen: null // laufender/abgeschlossener Fetch - verhindert Mehrfach-Requests
    }
    geteilterCache.set(cacheKey, eintrag)
  }
  return eintrag
}

async function tatsaechlichLaden(eintrag, apiBase, von, bis, bildBasisUrl) {
  eintrag.loading.value = true
  eintrag.error.value = null

  try {
    const params = new URLSearchParams({ from: von, to: bis })
    const res = await fetch(`${apiBase}/widget/get-stories-from-to?${params}`)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const rohListe = await res.json()

    eintrag.stories.value = (Array.isArray(rohListe) ? rohListe : []).map(roh => ({
      // GEÄNDERT: id und matchId waren bisher vermischt (id: roh.matchId ?? roh.id) - dadurch
      // war nicht mehr unterscheidbar, ob eine Story überhaupt zu einem Match gehört. Jetzt
      // getrennt: id ist IMMER die eigene Story-ID (fürs Vue-Key/Suche/generelle Identität),
      // matchId ist NULL bei eigenständigen Storys ohne Spielbezug (Transfer, Verletzung, etc.)
      // - genau DAS Feld, an dem oeffneStory() jetzt unterscheidet, ob ein Match geladen werden
      // muss oder nicht.
      id: roh.id,
      matchId: roh.matchId ?? null,
      text: roh.text ?? roh.story ?? '',
      imageBetman: roh.imageBetman ?? null,
      heim: roh.heim ?? roh.homeTeam ?? '',
      auswaerts: roh.auswaerts ?? roh.awayTeam ?? '',
      quote_1: roh.quote_1 ?? null,
      quote_X: roh.quote_X ?? null,
      quote_2: roh.quote_2 ?? null,
      timeMatch: roh.timeMatch ?? null,
      createdAt: roh.createdAt ?? null,
      phase: roh.phase ?? null, // 'preview' oder 'report' - kommt direkt von der DB
      completed: roh.completed ?? false,
      result: roh.result ?? null, // bereits fertig formatierter String, z.B. "3:1"
      scoresLive: roh.scoresLive ?? null, // OddsMatchScores - fürs Live-Erkennen
      flaggeBild: vollstaendigeBildUrl(roh.flag, bildBasisUrl), // Länderflagge, analog zu match.ligaFlaggeBild
      ligaId: roh.ligaId ?? null, // fürs Bestimmen der Flaggen-Größe (siehe lib/ligaTypen.js: flaggenGroesse)
      liga: roh.liga ?? null // Liga-Name, fürs Anzeigen in der erweiterten Karte
    }))
  } catch (e) {
    eintrag.error.value = 'Stories konnten nicht geladen werden.'
    console.warn('ladeStories fehlgeschlagen:', e)
    throw e
  } finally {
    eintrag.loading.value = false
  }
}

// Reaktiv ans Widget gebunden - fürs Laden der Story-REIHE mit fixem Zeitraum aus den Props.
export function useBetmanStories(props) {
  const von = props.von ?? heutePlusTage(-1)
  const bis = props.bis ?? heutePlusTage(90)
  const cacheKey = ermittleCacheKey({ apiBase: props.apiBase, von, bis, bildBasisUrl: props.bildBasisUrl })
  const eintrag = ermittleOderErstelleEintrag(cacheKey)
  const { stories, loading, error } = eintrag

  function ladeStories() {
    if (!eintrag.ladeversprechen) {
      eintrag.ladeversprechen = tatsaechlichLaden(eintrag, props.apiBase, von, bis, props.bildBasisUrl).catch(() => {
        // Fehlgeschlagenes Ladeversprechen NICHT im Cache behalten - ein erneuter Versuch
        // (z.B. Retry-Button) soll wirklich neu fetchen dürfen.
        eintrag.ladeversprechen = null
      })
    }
    return eintrag.ladeversprechen
  }

  return { stories, loading, error, ladeStories }
}

// Einmaliger Abruf für EINEN BELIEBIGEN Zeitraum - fürs interaktive Filtern in der Vollbild-
// Übersicht (Nutzer wählt "letzte Woche"/"letzter Monat"/eigene Daten). Nutzt denselben
// geteilten Cache wie useBetmanStories - identische Zeiträume (z.B. Übersicht + Reihe zufällig
// gleich eingestellt) treffen automatisch denselben Cache-Eintrag, kein doppelter Request.
export async function ladeStoriesFuerZeitraum(apiBase, bildBasisUrl, von, bis) {
  const cacheKey = ermittleCacheKey({ apiBase, von, bis, bildBasisUrl })
  const eintrag = ermittleOderErstelleEintrag(cacheKey)

  if (!eintrag.ladeversprechen) {
    eintrag.ladeversprechen = tatsaechlichLaden(eintrag, apiBase, von, bis, bildBasisUrl).catch(() => {
      eintrag.ladeversprechen = null
    })
  }
  await eintrag.ladeversprechen
  return { stories: eintrag.stories, loading: eintrag.loading, error: eintrag.error }
}

export { heutePlusTage }